import React, { useEffect, useRef, useState } from 'react';
import Icon from '@salesforce/design-system-react/components/icon';
import Tooltip from '@salesforce/design-system-react/components/tooltip';

type StepId = string | number;

export interface IProgressStep {
	id?: StepId;
	/** The step title. Any node is accepted, e.g. a plain string or a custom label component. */
	label: React.ReactNode;
	/** Optional details (HTML) shown beneath the label, on their own row, so they do not move the status circle. */
	description?: string;
	/** Screen reader text for the circle. Defaults to `Step <n>: <label> - <status>`. */
	assistiveText?: React.ReactNode;
	/** Rendered on the right of the step, centred against the label and the description, e.g. a toggle. */
	onRenderSetupAssistantAction?: React.ReactNode;
	/** Small grey text shown beneath the action on the right. */
	setupAssistantEstimatedTime?: React.ReactNode;
}

interface IStepState {
	isCompleted: boolean;
	isDisabled: boolean;
	isError: boolean;
	isSelected: boolean;
	step: IProgressStep;
}

interface IAssistiveText {
	step: string;
	completedStep: string;
	disabledStep: string;
	errorStep: string;
}

interface IProgressIndicator {
	id?: string;
	className?: string;
	steps: IProgressStep[];
	/** Steps (or `{ id }` objects) that are completed. */
	completedSteps?: { id?: StepId }[];
	/** Steps (or `{ id }` objects) that are disabled. They are not clickable or focusable. */
	disabledSteps?: { id?: StepId }[];
	/** Steps (or `{ id }` objects) that have an error. */
	errorSteps?: { id?: StepId }[];
	/** The currently active step. */
	selectedStep?: { id?: StepId, label?: React.ReactNode };
	/** When set, the status circle is a button and this is called on click with the step state. */
	onStepClick?: (event: React.SyntheticEvent, data: IStepState) => void;
	/** When set, this is called when the status circle is focused. */
	onStepFocus?: (event: React.SyntheticEvent, data: IStepState) => void;
	assistiveText?: Partial<IAssistiveText>;
}

const defaultAssistiveText: IAssistiveText = {
	step: 'Step',
	completedStep: 'Completed',
	disabledStep: 'Disabled',
	errorStep: 'Error',
};

/**
 * A step description clamped with an ellipsis. Hovering shows the full description as formatted HTML in a tooltip,
 * but only when the text is actually cut off.
 * The tooltip trigger defaults to an inline-block with line-height 1, which shrinks the width and squeezes wrapped lines,
 * so it is reset with `triggerStyle`.
 * The tooltip stays open while the cursor is over it and closes shortly after the cursor leaves both the text and the
 * tooltip, so that links in it can be clicked.
 */
function StepDescription({ id, html }: { id: string, html: string }) {
	const textRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const keepOpen = () => {
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
		}
	};
	const show = () => {
		keepOpen();
		const el = textRef.current;
		setIsOpen(!!el && el.scrollHeight > el.clientHeight);
	};
	const hideSoon = () => {
		keepOpen();
		closeTimer.current = setTimeout(() => setIsOpen(false), 300);
	};
	useEffect(() => keepOpen, []);

	return (
		// Tooltip overwrites the mouse handlers of its child, so they are on this wrapper.
		<div onMouseEnter={show} onMouseLeave={hideSoon}>
			<Tooltip
				id={id}
				align="top left"
				isOpen={isOpen}
				position="overflowBoundaryElement"
				triggerStyle={{ display: 'block', lineHeight: 'inherit' }}
				content={<div className="slds-current-color" onMouseEnter={keepOpen} onMouseLeave={hideSoon} dangerouslySetInnerHTML={{ __html: html }} />}
			>
				<div
					ref={textRef}
					className="slds-current-color slds-line-clamp_x-small slds-text-body_small slds-text-color_weak slds-p-left_small"
					dangerouslySetInnerHTML={{ __html: html }}
				/>
			</Tooltip>
		</div>
	);
}

/**
 * Whether `step` is in `items` (a list, or a single item), compared by id like the design-system-react
 * `ProgressIndicator` does.
 */
function findStep(step: IProgressStep, items?: { id?: StepId } | { id?: StepId }[]): boolean {
	if (!items) {
		return false;
	}
	const list = Array.isArray(items) ? items : [items];
	return list.some((item) => item === step || (item.id !== undefined && step.id !== undefined && item.id === step.id));
}

/**
 * Vertical setup-assistant progress indicator: a local clone of the design-system-react `ProgressIndicator`
 * (orientation="vertical", variant="setup-assistant") with the same SLDS markup, props and states, plus an optional
 * `description` on each step.
 *
 * Each step is a grid: on the left, a list with the circle and label on one row and the description on the next, so
 * the circle sits on the label row; on the right, the action, centred against both rows. Each step's list gets hidden
 * items at its ends so SLDS only treats the first and last steps of the whole indicator as first and last when drawing
 * the connector. Only SLDS classes are used, with no inline styles.
 *
 * @param {IProgressIndicator} props - The steps and their completed, disabled, error and selected state.
 * @returns {JSX.Element} The rendered progress indicator.
 */
function CQProgressIndicator(props: IProgressIndicator) {
	const assistiveText = { ...defaultAssistiveText, ...props.assistiveText };
	const lastIndex = props.steps.length - 1;

	return (
		<div id={props.id} className={['slds-progress', 'slds-progress_vertical', 'slds-progress_success', props.className].filter(Boolean).join(' ')}>
			{props.steps.map((step, index) => {
				const state: IStepState = {
					isCompleted: findStep(step, props.completedSteps),
					isDisabled: findStep(step, props.disabledSteps),
					isError: findStep(step, props.errorSteps),
					isSelected: findStep(step, props.selectedStep),
					step,
				};
				const { isCompleted, isDisabled, isError, isSelected } = state;
				const stepKey = step.id ?? index;
				const isFirst = index === 0;
				const isLast = index === lastIndex;
				const hasDescription = !!step.description;
				const hasAction = !!(step.onRenderSetupAssistantAction || step.setupAssistantEstimatedTime);
				const renderIcon = isCompleted || isError;
				const status = isError ? assistiveText.errorStep : isCompleted ? assistiveText.completedStep : isDisabled ? assistiveText.disabledStep : '';
				const stateClassName = [
					isCompleted ? 'slds-is-completed' : '',
					isSelected && !isError ? 'slds-is-active' : '',
					isError ? 'slds-has-error' : '',
				].filter(Boolean).join(' ');
				const markerClassName = [
					'slds-progress__marker',
					renderIcon ? 'slds-progress__marker_icon' : '',
					renderIcon && !isError ? 'slds-progress__marker_icon-success' : '',
				].filter(Boolean).join(' ');
				const markerContent = <>
					{renderIcon && <Icon category="utility" size="x-small" name={isError ? 'error' : 'success'} />}
					<span className="slds-assistive-text">
						{step.assistiveText || <>{`${assistiveText.step} ${index + 1}: `}{step.label}{status ? ` - ${status}` : ''}</>}
					</span>
				</>;
				const clickable = !isDisabled && !!props.onStepClick;
				const divider = isLast ? '' : 'slds-border_bottom';

				return (
					<div key={stepKey} className="slds-grid">
						<ol className="slds-progress__list slds-col">
							{!isFirst && <li className="slds-hide" aria-hidden="true" />}
							<li className={`slds-progress__item ${stateClassName}`.trim()}>
								{clickable ? (
									<button
										type="button"
										className={`slds-button ${markerClassName}`}
										onClick={(event) => props.onStepClick?.(event, state)}
										onFocus={(event) => props.onStepFocus?.(event, state)}
									>
										{markerContent}
									</button>
								) : (
									<span className={markerClassName}>{markerContent}</span>
								)}
								<div
									id={`progress-indicator-vertical-label-${stepKey}`}
									className={`slds-progress__item_content ${hasDescription ? '' : divider}`.trim()}
								>
									{/* Without a description the step is as tall as the library's: the label gets the same space above and below, so the circle stays on it. */}
									<div className={hasDescription ? '' : 'slds-p-vertical_small'}>{step.label}</div>
								</div>
							</li>
							{hasDescription && (
								// On the last step this row is not a connector item, so the line ends at the label row instead of trailing below the circle.
								<li className={isLast ? 'slds-grid' : `slds-progress__item ${stateClassName}`.trim()}>
									{/* Invisible circle that takes the same width as the real one, so the description lines up with the label. */}
									<span className="slds-progress__marker slds-hidden" aria-hidden="true" />
									<div className={`slds-col slds-p-bottom_small ${divider}`.trim()}>
										<StepDescription id={`progress-indicator-vertical-description-${stepKey}`} html={step.description as string} />
									</div>
								</li>
							)}
							{!isLast && <li className="slds-hide" aria-hidden="true" />}
						</ol>
						{hasAction && (
							<div className={`slds-grid slds-grid_vertical-align-center slds-grid_align-end slds-size_1-of-4 ${divider}`.trim()}>
								<div className="slds-media__figure slds-media__figure_reverse">
									{step.onRenderSetupAssistantAction}
									{step.setupAssistantEstimatedTime && (
										<p className="slds-text-align_right slds-text-color_weak slds-p-top_medium">{step.setupAssistantEstimatedTime}</p>
									)}
								</div>
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}

export default CQProgressIndicator;
